import { ETaskStatus } from '@constants';
import { ITask } from '@sdk/contracts';
import { dateProvider } from '@utils';
import { Avatar, Button, Divider, Flex, Popover, Tag } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PreviewActivity } from '../PreviewActivity';

import {
  ColumnWrapper,
  FooterWrapper,
  InfoWrapper,
  TaskHistoryWrapper,
  TitleWrapper,
  Wrapper
} from './TaskItem.styles';

import { Icon } from '@/components/Icon';
import { TaskStatusBadge } from '@/components/TaskStatusBadge';
import { useChangeStatusForm } from '@/hooks';
import { useActivityForm, useProfile, useTaskLogsModal } from '@/store';

type Props = {
  task: ITask;
};

export const TaskItem: React.FC<Props> = ({ task }) => {
  const { t } = useTranslation('CourseTranslations');
  const { setTask: setChangeStatusFormTask } = useChangeStatusForm();
  const { setIsOpen, setSectionId, setData, setCourse } = useActivityForm();
  const { setLogs } = useTaskLogsModal();
  const {
    roleCheck: { isTeacher, isManager }
  } = useProfile();

  const {
    title,
    deadline,
    statusInfo: { currentStatus, logs },
    assignee: { avatar, name, lastName },
    course
  } = task;

  const isEditDisabled = useMemo(
    () => isTeacher && currentStatus === ETaskStatus.APPROVED,
    [currentStatus, isTeacher]
  );
  const canPreviewActivity = useMemo(
    () => isManager && currentStatus === ETaskStatus.APPROVED,
    [currentStatus, isManager]
  );

  const section = useMemo(() => {
    return course?.sections.find(s => {
      return s.activities.some(a => a.task?._id === task._id);
    });
  }, [course, task]);
  const activity = useMemo(() => {
    return section?.activities.find(a => a.task?._id === task._id);
  }, [section, task]);

  const handleEditActivity = () => {
    if (!section || !activity) return;
    setCourse(course);
    setSectionId(section._id);
    setData(activity);
    setIsOpen(true);
  };

  const handleShowTaskLogs = () => setLogs(logs);

  const handleChangeStatus = () => {
    if (!isManager) return;
    setChangeStatusFormTask(task);
  };

  return (
    <Wrapper>
      <TitleWrapper>{title}</TitleWrapper>
      <Divider />
      <InfoWrapper>
        <ColumnWrapper>
          <div>{t('DEADLINE_DATE')}</div>
          <div>{dateProvider.format(deadline, 'dd/MM/yyyy')}</div>
        </ColumnWrapper>
        <ColumnWrapper>
          <div>{t('ASSIGNED_TO')}</div>
          <div>
            <Avatar size={24} src={avatar?.url}>
              {!avatar?.url && `${name[0]}${lastName[0]}`}
            </Avatar>
            <div>{`${name} ${lastName}`}</div>
          </div>
        </ColumnWrapper>
      </InfoWrapper>
      <InfoWrapper>
        <ColumnWrapper>
          <div>{t('SECTION')}</div>
          <Popover content={<div>{section?.name}</div>}>{section?.name}</Popover>
        </ColumnWrapper>
        <ColumnWrapper>
          <div>{t('COURSE')}</div>
          <Popover content={<Tag>{course.name}</Tag>}>
            <Tag>{course.name}</Tag>
          </Popover>
        </ColumnWrapper>
      </InfoWrapper>
      {isManager && (
        <TaskHistoryWrapper onClick={handleShowTaskLogs}>{t('TASK_HISTORY')}</TaskHistoryWrapper>
      )}
      <Divider />
      <FooterWrapper>
        {!isEditDisabled && (
          <Button size="small" type="link" icon={<Icon i="Edit" />} onClick={handleEditActivity}>
            {t('EDIT_CONTENT')}
          </Button>
        )}
        <Flex gap={4}>
          {canPreviewActivity && activity && <PreviewActivity activityId={activity._id} />}
          <TaskStatusBadge
            status={currentStatus}
            icon={currentStatus === ETaskStatus.IN_REVIEW ? 'Edit' : undefined}
            onClick={currentStatus === ETaskStatus.IN_REVIEW ? handleChangeStatus : undefined}
          />
        </Flex>
      </FooterWrapper>
    </Wrapper>
  );
};
